import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAdvertisement } from '../../contexts/AdvertisementContext';

/**
 * AdBanner Component
 * Notes+ - Bosph Technologies
 * 
 * Reklam banner komponenti. Belirli bir pozisyon için aktif reklamları gösterir.
 * 
 * @param {string} position - Reklam pozisyonu (sidebar, header, notes_list, modal)
 * @param {string} className - Ek CSS sınıfları
 * @param {boolean} showCloseButton - Kapatma butonu gösterilsin mi?
 * @param {function} onClose - Kapatma callback'i
 */
const AdBanner = ({ position, className = '', showCloseButton = false, onClose }) => {
  const { t } = useTranslation('shared');
  // Hook'ları her zaman en üstte çağırmalıyız (React Hooks Rules)
  // useAdvertisement hook'u context yoksa null döndürür (try-catch gerekmez)
  const advertisementContext = useAdvertisement();
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [dismissedAds, setDismissedAds] = useState(new Set());

  // Destructure context values (null-safe)
  const loadAdvertisements = advertisementContext?.loadAdvertisements;
  const getAdvertisements = advertisementContext?.getAdvertisements;
  const recordImpression = advertisementContext?.recordImpression;
  const recordClick = advertisementContext?.recordClick;

  // Tüm hook'ları erken return'den önce çağırmalıyız
  useEffect(() => {
    if (!advertisementContext || !position || !loadAdvertisements) return;
    // Reklamları yükle, hata olsa bile devam et (offline-first: sessizce handle edilir)
    loadAdvertisements(position).catch(() => {
      // Sessizce handle et - hatalar zaten AdvertisementContext içinde sessizce yönetiliyor
    });
  }, [position, loadAdvertisements, advertisementContext]);

  useEffect(() => {
    if (!advertisementContext || !getAdvertisements) return;
    const loadedAds = getAdvertisements(position);
    if (loadedAds && loadedAds.length > 0) {
      setAds(loadedAds);
      
      // İlk reklamın gösterimini kaydet
      if (loadedAds[0] && recordImpression) {
        recordImpression(loadedAds[0].id);
      }
    }
  }, [position, getAdvertisements, recordImpression, advertisementContext]);

  // Otomatik geçiş (5 saniyede bir)
  useEffect(() => {
    if (!advertisementContext || ads.length <= 1 || !recordImpression) return;

    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => {
        const nextIndex = (prev + 1) % ads.length;
        // Yeni reklamın gösterimini kaydet
        if (ads[nextIndex]) {
          recordImpression(ads[nextIndex].id);
        }
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [ads, recordImpression, advertisementContext]);

  // Context null ise render etme (hook'lardan sonra kontrol et)
  if (!advertisementContext) {
    return null;
  }

  const handleClose = (adId) => {
    setDismissedAds((prev) => new Set([...prev, adId]));
    onClose?.(adId);
  };

  const handleClick = (ad) => {
    if (recordClick) {
      recordClick(ad.id);
    }
    if (ad.linkUrl) {
      window.open(ad.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!ads || ads.length === 0) {
    return null;
  }

  // Dismissed olanları filtrele
  const visibleAds = ads.filter((ad) => !dismissedAds.has(ad.id));
  if (visibleAds.length === 0) {
    return null;
  }

  const currentAd = visibleAds[currentAdIndex % visibleAds.length];

  if (!currentAd) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`relative ${className}`}
    >
      <div
        onClick={() => handleClick(currentAd)}
        className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 cursor-pointer group shadow-lg hover:shadow-xl transition-all duration-300 min-h-[80px]"
      >
        {currentAd.imageUrl ? (
          <img
            src={currentAd.imageUrl}
            alt={currentAd.title || t('adBanner.ad')}
            className="w-full h-full object-cover min-h-[80px]"
          />
        ) : (
          <div className="p-4 min-h-[80px] flex flex-col justify-center">
            {currentAd.title && (
              <h3 className="text-white font-semibold text-sm mb-1">{currentAd.title}</h3>
            )}
            {currentAd.description && (
              <p className="text-white/90 text-xs">{currentAd.description}</p>
            )}
            {!currentAd.title && !currentAd.description && (
              <p className="text-white/80 text-xs">{t('adBanner.ad')}</p>
            )}
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

        {/* External link icon */}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ExternalLink className="w-4 h-4 text-white" />
        </div>

        {/* Close button */}
        {showCloseButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose(currentAd.id);
            }}
            className="absolute top-2 right-2 p-1 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
            aria-label={t('adBanner.close')}
          >
            <X className="w-3 h-3" />
          </button>
        )}

        {/* Multiple ads indicator */}
        {visibleAds.length > 1 && (
          <div className="absolute bottom-2 left-2 flex space-x-1">
            {visibleAds.map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full ${
                  index === currentAdIndex % visibleAds.length
                    ? 'bg-white'
                    : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AdBanner;

